/**
 * OneEuroFilter - Filtro de suavizado para tracking de manos
 * Basado en el algoritmo de Géry Casiez et al.
 * 
 * El filtro One Euro combina un filtro de paso bajo con adaptación dinámica
 * del parámetro alpha basado en la velocidad del movimiento.
 * 
 * Referencia: http://www.lifl.fr/~casiez/1euro/
 */

// ═══════════════════════════════════════════════════════════════════════════════
// FILTRO DE PASO BAJO
// ═══════════════════════════════════════════════════════════════════════════════

class LowPassFilter {
  private y: number | null = null;
  private s: number | null = null;
  private alpha: number;

  constructor(alpha: number) {
    this.alpha = alpha;
  }

  filter(value: number, alpha: number): number {
    this.alpha = alpha;
    
    if (this.y === null) {
      this.s = value;
      this.y = value;
      return value;
    }

    this.s = alpha * value + (1 - alpha) * this.s!;
    return this.s;
  }

  setAlpha(alpha: number): void {
    if (alpha <= 0 || alpha > 1) {
      throw new Error('Alpha must be in (0, 1]');
    }
    this.alpha = alpha;
  }

  lastValue(): number {
    return this.y ?? 0;
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ONE EURO FILTER
// ═══════════════════════════════════════════════════════════════════════════════

export interface OneEuroFilterConfig {
  minCutoff: number;      // Frecuencia de corte mínima
  beta: number;           // Velocidad de adaptación
  dCutoff: number;        // Frecuencia de corte para derivada
}

export class OneEuroFilter {
  private xFilter: LowPassFilter;
  private dxFilter: LowPassFilter;
  
  private minCutoff: number;
  private beta: number;
  private dCutoff: number;
  
  private lastTime: number | null = null;
  private lastRawValue: number | null = null;

  constructor(config?: Partial<OneEuroFilterConfig>) {
    this.minCutoff = config?.minCutoff ?? 1.0;
    this.beta = config?.beta ?? 0.0;
    this.dCutoff = config?.dCutoff ?? 1.0;
    
    this.xFilter = new LowPassFilter(this.alpha(this.minCutoff));
    this.dxFilter = new LowPassFilter(this.alpha(this.dCutoff));
  }

  /**
   * Calcula el parámetro alpha dado una frecuencia de corte
   */
  private alpha(cutoff: number): number {
    const te = 1.0; // Período de muestreo asumido (será actualizado)
    const tau = 1.0 / (2 * Math.PI * cutoff);
    return 1.0 / (1.0 + tau / te);
  }

  /**
   * Filtra un nuevo valor
   * @param value - Valor a filtrar
   * @param timestamp - Timestamp opcional (ms)
   * @returns Valor filtrado
   */
  filter(value: number, timestamp?: number): number {
    const currentTime = timestamp ?? performance.now();
    
    // Inicializar en primera llamada
    if (this.lastTime === null) {
      this.lastTime = currentTime;
      this.lastRawValue = value;
      return value;
    }

    // Calcular delta de tiempo
    const dt = (currentTime - this.lastTime) / 1000; // segundos
    this.lastTime = currentTime;

    // Evitar división por cero
    if (dt === 0) {
      return this.xFilter.lastValue();
    }

    // Calcular derivada
    const dx = (value - (this.lastRawValue ?? value)) / dt;
    this.lastRawValue = value;

    // Filtrar derivada
    const edx = this.dxFilter.filter(dx, this.alpha(this.dCutoff));
    
    // Calcular frecuencia de corte adaptativa
    const cutoff = this.minCutoff + this.beta * Math.abs(edx);
    
    // Filtrar valor
    const smoothedValue = this.xFilter.filter(value, this.alpha(cutoff));
    
    return smoothedValue;
  }

  /**
   * Resetea el filtro
   */
  reset(): void {
    this.lastTime = null;
    this.lastRawValue = null;
    // Recrear filtros internos
    this.xFilter = new LowPassFilter(this.alpha(this.minCutoff));
    this.dxFilter = new LowPassFilter(this.alpha(this.dCutoff));
  }

  /**
   * Actualiza parámetros del filtro
   */
  setParameters(params: Partial<OneEuroFilterConfig>): void {
    if (params.minCutoff !== undefined) {
      this.minCutoff = params.minCutoff;
    }
    if (params.beta !== undefined) {
      this.beta = params.beta;
    }
    if (params.dCutoff !== undefined) {
      this.dCutoff = params.dCutoff;
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// FILTRO 3D (para posiciones)
// ═══════════════════════════════════════════════════════════════════════════════

import * as THREE from 'three';

export class OneEuroVector3Filter {
  private xFilter: OneEuroFilter;
  private yFilter: OneEuroFilter;
  private zFilter: OneEuroFilter;

  constructor(config?: Partial<OneEuroFilterConfig>) {
    const filterConfig = {
      minCutoff: config?.minCutoff ?? 1.0,
      beta: config?.beta ?? 0.0,
      dCutoff: config?.dCutoff ?? 1.0,
    };
    
    this.xFilter = new OneEuroFilter(filterConfig);
    this.yFilter = new OneEuroFilter(filterConfig);
    this.zFilter = new OneEuroFilter(filterConfig);
  }

  filter(vector: THREE.Vector3, timestamp?: number): THREE.Vector3 {
    return new THREE.Vector3(
      this.xFilter.filter(vector.x, timestamp),
      this.yFilter.filter(vector.y, timestamp),
      this.zFilter.filter(vector.z, timestamp)
    );
  }

  filterInPlace(vector: THREE.Vector3, timestamp?: number): void {
    vector.x = this.xFilter.filter(vector.x, timestamp);
    vector.y = this.yFilter.filter(vector.y, timestamp);
    vector.z = this.zFilter.filter(vector.z, timestamp);
  }

  reset(): void {
    this.xFilter.reset();
    this.yFilter.reset();
    this.zFilter.reset();
  }

  setParameters(params: Partial<OneEuroFilterConfig>): void {
    this.xFilter.setParameters(params);
    this.yFilter.setParameters(params);
    this.zFilter.setParameters(params);
  }
}

// ═══════════════════════════════════════════════════════════════════════════════
// FACTORY PARA MÚLTIPLES LANDMARKS
// ═══════════════════════════════════════════════════════════════════════════════

export class HandFilterSet {
  private positionFilters: Map<number, OneEuroVector3Filter> = new Map();
  private config: OneEuroFilterConfig;

  constructor(config?: Partial<OneEuroFilterConfig>) {
    this.config = {
      minCutoff: config?.minCutoff ?? 1.2,
      beta: config?.beta ?? 0.01,
      dCutoff: config?.dCutoff ?? 1.0,
    };
  }

  filterLandmark(landmarkIndex: number, position: THREE.Vector3, timestamp?: number): THREE.Vector3 {
    if (!this.positionFilters.has(landmarkIndex)) {
      this.positionFilters.set(landmarkIndex, new OneEuroVector3Filter(this.config));
    }
    
    return this.positionFilters.get(landmarkIndex)!.filter(position, timestamp);
  }

  filterLandmarkInPlace(landmarkIndex: number, position: THREE.Vector3, timestamp?: number): void {
    if (!this.positionFilters.has(landmarkIndex)) {
      this.positionFilters.set(landmarkIndex, new OneEuroVector3Filter(this.config));
    }
    
    this.positionFilters.get(landmarkIndex)!.filterInPlace(position, timestamp);
  }

  reset(): void {
    this.positionFilters.forEach(filter => filter.reset());
  }

  setParameters(params: Partial<OneEuroFilterConfig>): void {
    this.positionFilters.forEach(filter => filter.setParameters(params));
  }
}

export default OneEuroFilter;
